var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _submenuMutationObserver, _deactivateTimer, _resizeListener, _overflowSubmenuListener, _onKeyDown, _state, _pointerIdleTimer, _lastPointer, _onPointerMove, _MegaMenuComponent_instances, reconcilePointerTarget_fn, startPointerTracking_fn, stopPointerTracking_fn, _deactivate, getOverflowListLinksHeight_fn, setFullOpenHeaderHeight_fn, _preloadImages, cleanupMutationObserver_fn;
import { Component } from "@theme/component";
import {
  debounce,
  onDocumentLoaded,
  setHeaderMenuStyle,
  setNavHoverSlideEnter,
  setNavHoverSlideExit
} from "@theme/utilities";
import { MegaMenuHoverEvent } from "@theme/events";
class MegaMenuComponent extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _MegaMenuComponent_instances);
    __publicField(this, "requiredRefs", ["overflowMenu"]);
    /**
     * @type {MutationObserver | null}
     */
    __privateAdd(this, _submenuMutationObserver, null);
    /**
     * Timer for delayed deactivation, allowing the pointer to cross the clip-path gap.
     * @type {ReturnType<typeof setTimeout> | undefined}
     */
    __privateAdd(this, _deactivateTimer);
    /**
     * Debounced resize event listener to recalculate menu style
     */
    __privateAdd(this, _resizeListener, debounce(() => {
      setHeaderMenuStyle();
    }, 100));
    __privateAdd(this, _overflowSubmenuListener, () => {
      __privateGet(this, _deactivate).call(this);
    });
    /**
     * Close the active submenu on Escape and return focus to the trigger.
     * @param {KeyboardEvent} event
     */
    __privateAdd(this, _onKeyDown, (event) => {
      if (event.key === "Escape" && __privateGet(this, _state).activeItem) {
        const link = __privateGet(this, _state).activeItem;
        __privateGet(this, _deactivate).call(this);
        link?.focus();
      }
    });
    /**
     * @type {State}
     */
    __privateAdd(this, _state, {
      activeItem: null
    });
    /**
     * @type {ReturnType<typeof setTimeout> | undefined}
     */
    __privateAdd(this, _pointerIdleTimer);
    /**
     * Last known pointer position for Safari hit-test reconciliation.
     * @type {{ x: number, y: number }}
     */
    __privateAdd(this, _lastPointer, { x: 0, y: 0 });
    /**
     * Update the safety box idle state on the active menu item.
     * @param {PointerEvent} event
     */
    __privateAdd(this, _onPointerMove, (event) => {
      const activeLink = __privateGet(this, _state).activeItem;
      if (!activeLink) return;
      __privateGet(this, _lastPointer).x = event.clientX;
      __privateGet(this, _lastPointer).y = event.clientY;
      const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
      activeLink.dataset.safetyBox = `${moving}`;
      clearTimeout(__privateGet(this, _pointerIdleTimer));
      if (moving) {
        __privateSet(this, _pointerIdleTimer, setTimeout(() => {
          if (__privateGet(this, _state).activeItem) {
            __privateGet(this, _state).activeItem.dataset.safetyBox = "false";
            __privateMethod(this, _MegaMenuComponent_instances, reconcilePointerTarget_fn).call(this);
          }
        }, 50));
      } else {
        __privateMethod(this, _MegaMenuComponent_instances, reconcilePointerTarget_fn).call(this);
      }
    });
    /**
     * Activate the selected menu item immediately
     * @param {PointerEvent | FocusEvent} event
     */
    __publicField(this, "activate", (event) => {
      this.dispatchEvent(new MegaMenuHoverEvent());
      if (!(event.target instanceof Element) || !this.headerComponent) return;
      let item = findMenuItem(event.target);
      if (!item) return;
      clearTimeout(__privateGet(this, _deactivateTimer));
      if (item == __privateGet(this, _state).activeItem) return;
      setNavHoverSlideEnter(item, event);
      const isDefaultSlot = event.target.slot === "";
      this.dataset.overflowExpanded = (!isDefaultSlot).toString();
      const previouslyActiveItem = __privateGet(this, _state).activeItem;
      if (previouslyActiveItem) {
        previouslyActiveItem.ariaExpanded = "false";
      }
      __privateGet(this, _state).activeItem = item;
      this.ariaExpanded = "true";
      item.ariaExpanded = "true";
      let submenu = findSubmenu(item);
      const hasSubmenu = Boolean(submenu);
      if (!hasSubmenu && !isDefaultSlot) {
        submenu = this.overflowMenu;
      }
      if (submenu) {
        submenu.dataset.active = "";
        __privateMethod(this, _MegaMenuComponent_instances, cleanupMutationObserver_fn).call(this);
        __privateSet(this, _submenuMutationObserver, new MutationObserver(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (submenu.offsetHeight > 0) {
                this.headerComponent?.style.setProperty(
                  "--submenu-height",
                  `${submenu.offsetHeight}px`
                );
                __privateMethod(this, _MegaMenuComponent_instances, cleanupMutationObserver_fn).call(this);
              }
            });
          });
        }));
        __privateGet(this, _submenuMutationObserver).observe(submenu, {
          childList: true,
          subtree: true
        });
        setTimeout(() => {
          __privateMethod(this, _MegaMenuComponent_instances, cleanupMutationObserver_fn).call(this);
        }, 2e3);
      }
      let finalHeight = submenu?.offsetHeight || 0;
      if (!isDefaultSlot) {
        const overflowListHeight = __privateMethod(this, _MegaMenuComponent_instances, getOverflowListLinksHeight_fn).call(this);
        if (hasSubmenu) {
          const overflowHeight = this.overflowMenu?.offsetHeight || 0;
          finalHeight = Math.max(overflowHeight, overflowListHeight);
        } else {
          finalHeight = overflowListHeight;
        }
      }
      if (!submenu) {
        finalHeight = 0;
      }
      this.headerComponent.style.setProperty(
        "--submenu-height",
        `${finalHeight}px`
      );
      __privateMethod(this, _MegaMenuComponent_instances, setFullOpenHeaderHeight_fn).call(this, finalHeight);
      this.style.setProperty("--submenu-opacity", "1");
      __privateMethod(this, _MegaMenuComponent_instances, startPointerTracking_fn).call(this, item, previouslyActiveItem);
    });
    /**
     * Deactivate the active item immediately
     * @param {HTMLElement | null} [item]
     */
    __privateAdd(this, _deactivate, (item = __privateGet(this, _state).activeItem) => {
      clearTimeout(__privateGet(this, _deactivateTimer));
      if (!item || item != __privateGet(this, _state).activeItem) return;
      if (this.overflowListHovered || this.overflowMenu?.matches(":hover"))
        return;
      this.headerComponent?.style.setProperty("--submenu-height", "0px");
      __privateMethod(this, _MegaMenuComponent_instances, setFullOpenHeaderHeight_fn).call(this, 0);
      this.style.setProperty("--submenu-opacity", "0");
      this.dataset.overflowExpanded = "false";
      const submenu = findSubmenu(item);
      document.body.removeEventListener("pointermove", __privateGet(this, _onPointerMove));
      __privateMethod(this, _MegaMenuComponent_instances, stopPointerTracking_fn).call(this, item);
      __privateGet(this, _state).activeItem = null;
      this.ariaExpanded = "false";
      item.ariaExpanded = "false";
      if (submenu) {
        delete submenu.dataset.active;
      }
    });
    /**
     * Preload images that are set to load lazily.
     */
    __privateAdd(this, _preloadImages, () => {
      const images = this.querySelectorAll('img[loading="lazy"]');
      images?.forEach((image) => image.removeAttribute("loading"));
    });
  }
  connectedCallback() {
    super.connectedCallback();
    onDocumentLoaded(__privateGet(this, _preloadImages));
    window.addEventListener("resize", __privateGet(this, _resizeListener));
    this.addEventListener("keydown", __privateGet(this, _onKeyDown));
    this.overflowMenu?.addEventListener(
      "pointerleave",
      __privateGet(this, _overflowSubmenuListener)
    );
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(__privateGet(this, _deactivateTimer));
    window.removeEventListener("resize", __privateGet(this, _resizeListener));
    this.removeEventListener("keydown", __privateGet(this, _onKeyDown));
    document.body.removeEventListener("pointermove", __privateGet(this, _onPointerMove));
    if (__privateGet(this, _state).activeItem) {
      __privateMethod(this, _MegaMenuComponent_instances, stopPointerTracking_fn).call(this, __privateGet(this, _state).activeItem);
    }
    this.overflowMenu?.removeEventListener(
      "pointerleave",
      __privateGet(this, _overflowSubmenuListener)
    );
    __privateMethod(this, _MegaMenuComponent_instances, cleanupMutationObserver_fn).call(this);
  }
  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return (
      /** @type {HTMLElement | null} */
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }
  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(":hover") ?? false;
  }
  get headerComponent() {
    return (
      /** @type {HTMLElement | null} */
      this.closest("header-component")
    );
  }
  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;
    const leavingItem = findMenuItem(event.target);
    if (leavingItem) setNavHoverSlideExit(leavingItem, event);
    const menu = findSubmenu(__privateGet(this, _state).activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu = event.relatedTarget instanceof Node && event.type === "blur" && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu = event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');
    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (__privateGet(this, _state).activeItem) {
        __privateMethod(this, _MegaMenuComponent_instances, stopPointerTracking_fn).call(this, __privateGet(this, _state).activeItem);
      }
      return;
    }
    clearTimeout(__privateGet(this, _deactivateTimer));
    __privateSet(this, _deactivateTimer, setTimeout(() => __privateGet(this, _deactivate).call(this), 150));
  }
}
_submenuMutationObserver = new WeakMap();
_deactivateTimer = new WeakMap();
_resizeListener = new WeakMap();
_overflowSubmenuListener = new WeakMap();
_onKeyDown = new WeakMap();
_state = new WeakMap();
_pointerIdleTimer = new WeakMap();
_lastPointer = new WeakMap();
_onPointerMove = new WeakMap();
_MegaMenuComponent_instances = new WeakSet();
/**
 * Check if the pointer is over a different menu item and trigger activation if so.
 * Works around Safari not re-evaluating hit targets after pseudo-element changes.
 */
reconcilePointerTarget_fn = function() {
  const { x, y } = __privateGet(this, _lastPointer);
  requestAnimationFrame(() => {
    const target = document.elementFromPoint(x, y);
    if (!target) return;
    const listItem = target.closest(".menu-list__list-item");
    if (listItem && !listItem.contains(__privateGet(this, _state).activeItem)) {
      listItem.dispatchEvent(
        new PointerEvent("pointerenter", { bubbles: false })
      );
    }
  });
};
/**
 * Begin pointer tracking for the safety box on the newly active item.
 * @param {HTMLElement} item
 * @param {HTMLElement | null} previousItem
 */
startPointerTracking_fn = function(item, previousItem) {
  if (previousItem) {
    __privateMethod(this, _MegaMenuComponent_instances, stopPointerTracking_fn).call(this, previousItem);
  } else {
    document.body.addEventListener("pointermove", __privateGet(this, _onPointerMove));
  }
  const rect = item.getBoundingClientRect();
  const isOverlap = this.headerComponent?.hasAttribute(
    "data-submenu-overlap-bottom-row"
  );
  const boundary = isOverlap ? this.headerComponent?.querySelector(".header__row--top") : this.headerComponent;
  item.style.setProperty(
    "--box-height",
    `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`
  );
};
/**
 * Stop pointer tracking and remove all safety box properties from an item.
 * @param {HTMLElement} item
 */
stopPointerTracking_fn = function(item) {
  clearTimeout(__privateGet(this, _pointerIdleTimer));
  __privateSet(this, _pointerIdleTimer, void 0);
  item.style.removeProperty("--box-height");
  delete item.dataset.safetyBox;
};
_deactivate = new WeakMap();
getOverflowListLinksHeight_fn = function() {
  const slottedMenuLinks = this.overflowMenu?.querySelector("slot")?.assignedElements();
  if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;
  const mapSubmenus = (cb) => {
    slottedMenuLinks.forEach((link) => {
      const submenu = (
        /** @type {HTMLElement | null} */
        link.querySelector('[ref="submenu[]"]')
      );
      if (submenu) {
        cb(submenu);
      }
    });
  };
  mapSubmenus((submenu) => {
    submenu.style.setProperty("display", "none");
  });
  const height = this.overflowMenu?.offsetHeight || 0;
  mapSubmenus((submenu) => {
    submenu.style.removeProperty("display");
  });
  return height;
};
/**
 * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
 * @param {number} submenuHeight
 */
setFullOpenHeaderHeight_fn = function(submenuHeight) {
  if (!this.headerComponent) return;
  const isOverlapSituation = this.headerComponent.hasAttribute(
    "data-submenu-overlap-bottom-row"
  );
  const headerVisibleHeight = isOverlapSituation && this.headerComponent.offsetHeight > 0 ? (
    /** @type {HTMLElement | null} */
    this.headerComponent.querySelector(".header__row--top")?.offsetHeight ?? 0
  ) : this.headerComponent.offsetHeight;
  const nothingToOpen = submenuHeight === 0;
  const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);
  this.headerComponent?.style.setProperty(
    "--full-open-header-height",
    `${fullOpenHeaderHeight}px`
  );
};
_preloadImages = new WeakMap();
cleanupMutationObserver_fn = function() {
  __privateGet(this, _submenuMutationObserver)?.disconnect();
  __privateSet(this, _submenuMutationObserver, null);
};
if (!customElements.get("mega-menu-component")) {
  customElements.define("mega-menu-component", MegaMenuComponent);
}
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element?.matches('[slot="more"]')) {
    return findMenuItem(
      element.parentElement?.querySelector('[slot="overflow"]')
    );
  }
  return element?.querySelector('[ref="menuitem"]');
}
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
